<script setup>
/**
 * Shopify.
 *
 * Connecting needs the store's own myshopify.com domain before the OAuth
 * handshake can start, so the card's `action` slot opens a dialog that asks for
 * it. A failed handshake comes back as `?error=`, and the page says so in place
 * rather than sending the reader back to Apps to guess.
 */
import { ref, computed, onMounted } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import integrationAPI from 'dashboard/api/integrations';

import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Integration from './Integration.vue';

defineProps({
  error: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const integrationLoaded = ref(false);
const storeUrl = ref('');
const isSubmitting = ref(false);
const storeUrlError = ref('');
const integration = useFunctionGetter('integrations/getIntegration', 'shopify');
const uiFlags = useMapGetter('integrations/getUIFlags');

const isLoading = computed(
  () => !integrationLoaded.value || uiFlags.value.isCreatingShopify
);

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return 'connect';
});

const hideStoreUrlModal = () => {
  storeUrl.value = '';
  storeUrlError.value = '';
  isSubmitting.value = false;
};

const validateStoreUrl = url => {
  const pattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;
  return pattern.test(url);
};

const openStoreUrlDialog = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

const handleStoreUrlSubmit = async () => {
  try {
    storeUrlError.value = '';
    if (!validateStoreUrl(storeUrl.value)) {
      storeUrlError.value =
        'Please enter a valid Shopify store URL (e.g., your-store.myshopify.com)';
      return;
    }

    isSubmitting.value = true;
    const { data } = await integrationAPI.connectShopify({
      shopDomain: storeUrl.value,
    });

    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    }
  } catch (error) {
    storeUrlError.value = error.message;
  } finally {
    isSubmitting.value = false;
  }
};

const initializeShopifyIntegration = async () => {
  await store.dispatch('integrations/get', 'shopify');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeShopifyIntegration();
});
</script>

<template>
  <SettingsPageShell :title="$t('INTEGRATION_SETTINGS.SHOPIFY.HEADER')">
    <SettingsSection
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.LOADING')"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <template v-else>
      <Integration
        :integration-id="integration.id"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="integrationAction"
        :delete-confirmation-text="{
          title: t('INTEGRATION_SETTINGS.SHOPIFY.DELETE.TITLE'),
          message: t('INTEGRATION_SETTINGS.SHOPIFY.DELETE.MESSAGE'),
        }"
      >
        <template #action>
          <Button
            size="sm"
            variant="faded"
            color="blue"
            :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            @click="openStoreUrlDialog"
          />
        </template>
      </Integration>

      <SettingsSection v-if="error">
        <p class="px-4 py-3.5 mb-0 text-sm text-n-ruby-11">
          {{ t('INTEGRATION_SETTINGS.SHOPIFY.ERROR') }}
        </p>
      </SettingsSection>
    </template>

    <Dialog
      ref="dialogRef"
      :title="t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.TITLE')"
      :is-loading="isSubmitting"
      @confirm="handleStoreUrlSubmit"
      @close="hideStoreUrlModal"
    >
      <Input
        v-model="storeUrl"
        :label="t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.LABEL')"
        :placeholder="t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.PLACEHOLDER')"
        :message="
          !storeUrlError
            ? t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.HELP')
            : storeUrlError
        "
        :message-type="storeUrlError ? 'error' : 'info'"
      />
    </Dialog>
  </SettingsPageShell>
</template>
